import React, { useEffect } from "react";
import Footer from "../components/Footer";
import AOS from "aos";
import "aos/dist/aos.css";
import { Gift, Truck, Heart } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";

function About() {
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  return (
    <>
      {/* Breadcrumbs */}
      <div className="max-w-6xl mx-auto px-4">
        <Breadcrumbs />
      </div>

      {/* Hero Section */}
      <section className="text-center py-20 bg-blue-50">
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-4xl font-bold text-blue-700 mb-4">About Exudee</h1>
          <p className="text-gray-600 text-lg">
            We help people express love, celebrate moments, and send happiness — one curated hamper at a time.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-pink-50 via-white to-blue-50" data-aos="fade-up">
        <div className="max-w-5xl mx-auto">
          {/* Heading */}
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
              Our Story
            </h2>
          </div>

          {/* Content */}
          <div className="text-gray-700 leading-relaxed space-y-6">
            <p className="text-justify">
              Exudee Hampers was born from the simple idea that giving should be easy, thoughtful, 
              and impactful. With a passion for premium flowers, hand-picked accessories, and meaningful 
              gifting, we’ve helped thousands across Dallas and beyond create magical moments. Whether 
              it's a birthday, a surprise gesture, or a heartfelt thank you — we make gifting personal.
            </p>

            <p className="text-justify">
              Established in <span className="font-semibold">2025</span>, we're revolutionizing the art of 
              gift-giving. We understand the challenge of finding the perfect present, so we've created 
              a seamless solution that takes the stress out of gifting. Our same-day, on-the-go delivery 
              service ensures your thoughtful gifts arrive promptly, making every occasion memorable. 
              You can also plan with our next-day booking option, giving you peace of mind that your gift 
              will arrive exactly when it's needed.
            </p>

            <p className="text-justify">
              To make gifting even easier and more affordable, we've curated special packages designed 
              to suit every taste and budget. Choose from our signature collections:
            </p>

            {/* Packages List */}
            <ul className="list-disc list-inside space-y-2 text-gray-800 ml-5">
              <li>
                <span className="font-semibold">Blossom Delight:</span> A beautiful floral arrangement 
                paired with one carefully selected accessory.
              </li>
              <li>
                <span className="font-semibold">Charming Bloom:</span> An exquisite floral gift 
                complemented by two delightful accessories.
              </li>
              <li>
                <span className="font-semibold">VIP Opulence:</span> The ultimate gifting experience, 
                featuring a luxurious floral display and a selection of premium accessories.
              </li>
            </ul>

            {/* NB Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mt-6 shadow-sm">
              <h4 className="font-semibold text-blue-800 mb-3">NB:</h4>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-5">
                <li>Blossom Delight → Flower Gift + 1 Accessory</li>
                <li>Charming Bloom → Flower Gift + 2 Accessories</li>
                <li>VIP Opulence → Flower Gift + VIP Collections</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-white py-16" data-aos="fade-up">
        <h2 className="text-3xl font-bold mb-10 text-center">Values We Bring</h2>
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-3 gap-8 text-center">
          {[
            {
              icon: Gift,
              title: "Elegance in Every Hamper",
              desc: "Each box is curated to impress — beautiful, bold, and built to wow.",
            },
            {
              icon: Truck,
              title: "Free, Fast, Same-Day Delivery",
              desc: "We offer free same-day delivery for orders made before 3.00PM within 50 miles of Dallas so your gift always arrives on time.",
            },
            {
              icon: Heart,
              title: "Gifts That Speak",
              desc: "From romantic to luxurious, we help you express yourself without saying a word.",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="p-6 bg-blue-50 rounded shadow"
              data-aos="fade-up"
              data-aos-delay={i * 150}
            >
              <item.icon className="w-12 h-12 mx-auto text-blue-600 mb-4" />
              <h3 className="text-xl font-bold text-blue-600 mb-2">{item.title}</h3>
              <p className="text-gray-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 max-w-6xl mx-auto px-4" data-aos="fade-up">
        <h2 className="text-3xl font-bold text-center mb-8">Made for You</h2>
        <p className="text-center text-gray-700 max-w-3xl mx-auto">
          We’re here to make your life easier. Our team works behind the scenes to ensure your gifts are packed
          with love and delivered with care. From sourcing the finest flowers to selecting premium accessories,
          we never compromise on quality.
        </p>
      </section>

      {/* CTA */}
      <section className="text-center py-16 bg-blue-100" data-aos="fade-up">
        <h3 className="text-2xl font-bold text-blue-700 mb-4">Ready to make someone’s day?</h3>
        <a href="/build">
          <button className="bg-blue-600 text-white px-6 py-3 rounded hover:bg-blue-700 transition">
            Build Your Hamper
          </button>
        </a>
      </section>

      {/* Footer */}
      <div data-aos="fade-up">
        <Footer />
      </div>
    </>
  );
}

export default About;
